import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Badge,
  Card,
  DataTable,
  EmptyState,
  LoadingState,
  V2Button,
  V2Modal,
} from '@sakhtar/design-system/react';
import {
  addOperatorShortLinkHost,
  approveOperatorShortLinkPrint,
  createOperatorShortLink,
  listOperatorShortLinkHosts,
  listOperatorShortLinks,
  rehearseOperatorShortLink,
  releaseOperatorShortLinkPrintAssets,
  removeOperatorShortLinkHost,
  updateOperatorShortLink,
  verifyOperatorShortLink,
  type OperatorShortLink,
  type OperatorShortLinkHost,
  type OperatorShortLinksResult,
} from '../../lib/operatorApi.js';
import { ApiError } from '../../lib/apiClient.js';
import {
  SHORT_LINK_CODE,
  destinationBadge,
  destinationHint,
  printBadge,
  qrPngBlob,
  qrSvg,
  rehearsalBadge,
  suggestedFilename,
} from './shortLinksModel.js';
import { OperatorPage } from './OperatorShell.js';

const ltr = {
  direction: 'ltr' as const,
  unicodeBidi: 'plaintext' as const,
  textAlign: 'left' as const,
};

function download(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.envelope.error.details?.[0]?.message ?? error.message;
  return 'عملیات انجام نشد؛ دوباره تلاش کنید.';
}

/**
 * «لینک کوتاه و QR». The QR encodes ONLY `{base}/q/{code}` and never changes; the destination behind it
 * is edited here with no code change and no deploy. A code is permanent (never deleted, renamed or
 * silently reassigned). External https destinations work only on hosts approved in this page. A QR file
 * is offered only after the deployed short URL verified and the change-and-restore rehearsal passed.
 * A list that cannot be loaded shows an error — never stale or fallback data as if it were current.
 */
export function ShortLinksPage() {
  const [data, setData] = useState<OperatorShortLinksResult | null>(null);
  const [hosts, setHosts] = useState<readonly OperatorShortLinkHost[]>([]);
  const [error, setError] = useState<{ unavailable: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrFor, setQrFor] = useState<OperatorShortLink | null>(null);
  const [checksFor, setChecksFor] = useState<OperatorShortLink | null>(null);
  const [editing, setEditing] = useState<{ link: OperatorShortLink | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    void Promise.all([listOperatorShortLinks(), listOperatorShortLinkHosts()])
      .then(([result, hostList]) => {
        setData(result);
        setHosts(hostList);
      })
      .catch((err: unknown) => {
        // Drop whatever we showed before: it is no longer known to be current.
        setData(null);
        setHosts([]);
        setError({ unavailable: err instanceof ApiError && err.status === 503 });
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  async function act(link: OperatorShortLink, run: () => Promise<OperatorShortLink>, done: string) {
    setBusyCode(link.code);
    setRowError(null);
    setNotice(null);
    try {
      const updated = await run();
      setNotice(done);
      const failed = updated.rehearsal && !updated.rehearsal.pass ? updated : null;
      setChecksFor(failed ?? (updated.verification && !updated.verification.pass ? updated : null));
    } catch (err) {
      setRowError(errorText(err));
      if (err instanceof ApiError && err.envelope.error.details?.length) {
        setChecksFor({
          ...link,
          verification: {
            pass: false,
            checkedAt: new Date().toISOString(),
            revision: link.revision,
            checks: err.envelope.error.details.map((d) => ({
              id: d.path,
              pass: false,
              detail: d.message,
            })),
          },
        });
      }
    } finally {
      setBusyCode(null);
      load();
    }
  }

  const base = data?.publicBaseUrl ?? '';

  return (
    <OperatorPage
      title="لینک کوتاه و QR"
      actions={
        <V2Button
          variant="primary"
          onClick={() => setEditing({ link: null })}
          disabled={data === null}
        >
          کد جدید
        </V2Button>
      }
    >
      <Alert tone="info">
        QR فقط آدرس دائمی <bdi style={ltr}>{base || '…'}/q/code</bdi> را دارد و هیچ‌وقت عوض نمی‌شود؛
        مقصد را از همین‌جا بدون تغییر کد و بدون استقرار عوض کنید. کد دائمی است: حذف نمی‌شود، تغییر
        نام نمی‌دهد و با «کد جدید» نمی‌توان کد موجود را از نو تعریف کرد. فایل QR فقط بعد از تأیید
        آدرس مستقرشده و موفق‌شدن «آزمون تغییر و بازگردانی» صادر می‌شود.
      </Alert>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {rowError ? <Alert tone="danger">{rowError}</Alert> : null}

      {error ? (
        <Alert tone="danger">
          <strong>فهرست لینک‌ها بارگذاری نشد.</strong>{' '}
          {error.unavailable
            ? 'پایگاه‌داده در دسترس نیست؛ وضعیت فعلی قابل اعتماد نیست و عمداً نمایش داده نمی‌شود (داده‌های ذخیره‌شدهٔ قبلی را به‌جای وضعیت فعلی نشان نمی‌دهیم).'
            : 'دسترسی لازم را ندارید یا خطایی رخ داد.'}{' '}
          <V2Button size="sm" variant="secondary" onClick={load}>
            تلاش دوباره
          </V2Button>
        </Alert>
      ) : null}
      {loading && data === null && !error ? <LoadingState /> : null}

      {data ? <ResolverHealth data={data} /> : null}
      {data && base === '' ? (
        <Alert tone="warning">
          آدرس عمومی سامانه (APP_BASE_URL) تنظیم نشده است؛ بررسی و QR تا تنظیم آن ممکن نیست.
        </Alert>
      ) : null}

      {data ? (
        data.shortLinks.length === 0 ? (
          <EmptyState title="هنوز کدی ثبت نشده است." />
        ) : (
          <Card>
            <DataTable
              columns={[
                {
                  key: 'code',
                  header: 'کد',
                  render: (row: OperatorShortLink) => (
                    <div>
                      <strong style={ltr}>{row.code}</strong>
                      <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                        نسخهٔ {row.revision}
                      </div>
                      {row.note ? (
                        <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{row.note}</div>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'target',
                  header: 'مقصد',
                  render: (row: OperatorShortLink) => (
                    <div>
                      <Badge tone={row.destination.kind === 'external' ? 'info' : 'default'}>
                        {row.destination.kind === 'external' ? 'خارجی' : 'داخلی'}
                      </Badge>{' '}
                      <span style={{ ...ltr, fontSize: 13, wordBreak: 'break-all' }}>
                        {row.targetPath}
                      </span>
                      {row.destination.hostAllowed === false ? (
                        <div>
                          <Badge tone="danger">میزبان دیگر تأیید نیست؛ هدایت متوقف شده</Badge>
                        </div>
                      ) : null}
                      {row.destination.kind === 'internal' && !row.tenant.exists ? (
                        <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                          توجه: سازمانی با این اسلاگ هنوز ساخته نشده (فقط اطلاع؛ مانع چاپ نیست).
                        </div>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'states',
                  header: 'وضعیت',
                  render: (row: OperatorShortLink) => {
                    const d = destinationBadge(row.state.destination);
                    const r = rehearsalBadge(row.state.rehearsal);
                    const p = printBadge(row.state.print, row.state.printReady);
                    return (
                      <div style={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
                        <Badge tone={row.status === 'active' ? 'success' : 'default'}>
                          {row.status === 'active' ? 'فعال' : 'غیرفعال'}
                        </Badge>
                        <Badge tone={d.tone}>آدرس مستقرشده: {d.label}</Badge>
                        <Badge tone={r.tone}>{r.label}</Badge>
                        <Badge tone={p.tone}>چاپ: {p.label}</Badge>
                        {row.verification || row.rehearsal ? (
                          <button
                            type="button"
                            className="sk-link"
                            onClick={() => setChecksFor(row)}
                          >
                            جزئیات بررسی
                          </button>
                        ) : null}
                      </div>
                    );
                  },
                },
                {
                  key: 'actions',
                  header: '',
                  render: (row: OperatorShortLink) => {
                    const busy = busyCode === row.code || base === '';
                    return (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <V2Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() =>
                            void act(
                              row,
                              () => verifyOperatorShortLink(row.code),
                              'آدرس مستقرشده بررسی شد.',
                            )
                          }
                        >
                          بررسی آدرس مستقرشده
                        </V2Button>
                        <V2Button
                          size="sm"
                          variant="secondary"
                          disabled={busy || row.status !== 'active' || row.firstReleasedAt !== null}
                          title={
                            row.firstReleasedAt !== null
                              ? 'این کد قبلاً برای چاپ صادر شده؛ آزمون روی کدی که ممکن است اسکن شود انجام نمی‌شود.'
                              : 'مقصد را موقتاً عوض می‌کند، می‌سنجد و برمی‌گرداند (فقط قبل از اولین چاپ).'
                          }
                          onClick={() =>
                            void act(
                              row,
                              () => rehearseOperatorShortLink(row.code),
                              'آزمون تغییر و بازگردانی انجام شد.',
                            )
                          }
                        >
                          آزمون تغییر و بازگردانی
                        </V2Button>
                        <V2Button
                          size="sm"
                          variant="secondary"
                          disabled={busy || row.state.print === 'approved'}
                          onClick={() =>
                            void act(
                              row,
                              () => approveOperatorShortLinkPrint(row.code),
                              'چاپ تأیید شد.',
                            )
                          }
                        >
                          تأیید چاپ
                        </V2Button>
                        <V2Button
                          size="sm"
                          variant="primary"
                          disabled={!row.state.printReady}
                          onClick={() => setQrFor(row)}
                        >
                          QR برای چاپ
                        </V2Button>
                        <V2Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing({ link: row })}
                        >
                          ویرایش مقصد
                        </V2Button>
                      </div>
                    );
                  },
                },
              ]}
              rows={[...data.shortLinks]}
              rowKey={(row: OperatorShortLink) => row.code}
            />
          </Card>
        )
      ) : null}

      {data ? <HostsCard hosts={hosts} onChanged={load} /> : null}

      {qrFor ? (
        <QrDialog
          link={qrFor}
          onClose={() => {
            setQrFor(null);
            load();
          }}
        />
      ) : null}
      {checksFor ? <ChecksDialog link={checksFor} onClose={() => setChecksFor(null)} /> : null}
      {editing ? (
        <EditDialog
          link={editing.link}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setNotice(
              `کد ${saved.code} ذخیره شد (نسخهٔ ${saved.revision}). مقصد جدید همین حالا برای همهٔ QRهای چاپ‌شده اعمال می‌شود؛ برای چاپ، آدرس مستقرشده را دوباره بررسی و تأیید کنید.`,
            );
            load();
          }}
        />
      ) : null}
    </OperatorPage>
  );
}

function ResolverHealth({ data }: { readonly data: OperatorShortLinksResult }) {
  const r = data.resolver;
  const degraded = r.lastRefreshError !== null || r.lkgServed > 0 || r.unavailableServed > 0;
  return (
    <Alert tone={degraded ? 'warning' : 'success'}>
      <strong>پاسخ‌دهی عمومی (/q):</strong> {r.snapshotCodes} کد در حافظهٔ آخرین-وضعیت-تأییدشده
      {r.lastFullRefreshAt
        ? ` · آخرین به‌روزرسانی ${new Date(r.lastFullRefreshAt).toLocaleString('fa-IR')}`
        : ' · هنوز به‌روز نشده'}
      {r.lkgServed > 0 ? ` · ${r.lkgServed} اسکن از حافظه (هنگام قطعی پایگاه‌داده)` : ''}
      {r.unavailableServed > 0
        ? ` · ${r.unavailableServed} اسکن صفحهٔ «موقتاً در دسترس نیست» گرفته`
        : ''}
      {r.lastRefreshError ? ` · خطای آخرین به‌روزرسانی: ${r.lastRefreshError}` : ''}
      <div style={{ fontSize: 12 }}>
        این فقط سلامت پاسخ‌دهی عمومی است، نه وضعیت مرجع. اگر پایگاه‌داده قطع باشد، فقط آخرین وضعیت
        تأییدشده تا {Math.round(r.lkgMaxAgeSeconds / 60)} دقیقه استفاده می‌شود و بعد از آن به‌جای
        هدایت اشتباه، صفحهٔ بازیابی نشان داده می‌شود.
      </div>
    </Alert>
  );
}

function HostsCard({
  hosts,
  onChanged,
}: {
  readonly hosts: readonly OperatorShortLinkHost[];
  readonly onChanged: () => void;
}) {
  const [host, setHost] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setHost('');
      setNote('');
      onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="میزبان‌های مجاز برای مقصد خارجی (https)">
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          یک کد فقط به آدرس https روی میزبانی می‌تواند برود که اینجا تأیید شده باشد (تطابق دقیق،
          بدون wildcard). این فهرست مانع «open redirect» است و هر تغییرش ثبت می‌شود.
        </p>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {hosts.length === 0 ? <p style={{ margin: 0 }}>هنوز میزبانی تأیید نشده است.</p> : null}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {hosts.map((h) => (
            <li
              key={h.host}
              style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <strong style={ltr}>{h.host}</strong>
              {h.note ? (
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{h.note}</span>
              ) : null}
              {h.usedBy.length > 0 ? (
                <Badge tone="info">در استفادهٔ: {h.usedBy.join('، ')}</Badge>
              ) : null}
              <V2Button
                size="sm"
                variant="ghost"
                disabled={busy || h.usedBy.length > 0}
                title={h.usedBy.length > 0 ? 'ابتدا مقصد کدهای مربوط را عوض کنید' : undefined}
                onClick={() => void run(() => removeOperatorShortLinkHost(h.host))}
              >
                حذف تأیید
              </V2Button>
            </li>
          ))}
        </ul>
        <form
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void run(() =>
              addOperatorShortLinkHost(
                host.trim().toLowerCase(),
                note.trim() === '' ? null : note.trim(),
              ),
            );
          }}
        >
          <label style={{ display: 'grid', gap: 4 }}>
            <span>میزبان جدید</span>
            <input
              className="sk-input"
              style={ltr}
              value={host}
              placeholder="www.nashmi-salon.ir"
              onChange={(e) => setHost(e.target.value)}
            />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span>یادداشت</span>
            <input
              className="sk-input"
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <V2Button variant="primary" type="submit" disabled={busy || host.trim() === ''}>
            تأیید میزبان
          </V2Button>
        </form>
      </div>
    </Card>
  );
}

function ChecksDialog({
  link,
  onClose,
}: {
  readonly link: OperatorShortLink;
  readonly onClose: () => void;
}) {
  const v = link.verification;
  const r = link.rehearsal;
  const mark = (pass: boolean, advisory?: boolean) => (
    <span
      aria-hidden="true"
      style={{
        color: pass ? 'var(--sk-success)' : advisory ? 'var(--sk-fg-muted)' : 'var(--sk-danger)',
        fontWeight: 700,
      }}
    >
      {pass ? '✓' : advisory ? 'ⓘ' : '✗'}
    </span>
  );
  return (
    <V2Modal open title="جزئیات بررسی" size="md" onClose={onClose}>
      <div style={{ display: 'grid', gap: 14 }}>
        <strong style={ltr}>{link.code}</strong>
        {v === null ? <p>آدرس مستقرشده هنوز بررسی نشده است.</p> : null}
        {v ? (
          <div>
            <h4 style={{ margin: '0 0 6px' }}>آدرس مستقرشده (نسخهٔ {v.revision})</h4>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {v.checks.map((c) => (
                <li key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  {mark(c.pass, c.advisory)}
                  <span>
                    <span style={{ ...ltr, fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      {c.id}
                      {c.advisory ? ' · فقط اطلاع' : ''}
                    </span>
                    <div>{c.detail}</div>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {r ? (
          <div>
            <h4 style={{ margin: '0 0 6px' }}>آزمون تغییر و بازگردانی</h4>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {r.steps.map((s) => (
                <li key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  {mark(s.pass)}
                  <span>
                    <span style={{ ...ltr, fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      {s.id}
                    </span>
                    <div style={ltr}>{s.detail}</div>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </V2Modal>
  );
}

/** Opened only for a print-ready code. The server re-checks live before releasing the address. */
function QrDialog({
  link,
  onClose,
}: {
  readonly link: OperatorShortLink;
  readonly onClose: () => void;
}) {
  const [released, setReleased] = useState<{ url: string; revision: number } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void releaseOperatorShortLinkPrintAssets(link.code)
      .then((r) => !cancelled && setReleased(r))
      .catch((err) => !cancelled && setProblem(errorText(err)));
    return () => {
      cancelled = true;
    };
  }, [link.code]);

  const svg = useMemo(() => (released ? qrSvg(released.url) : null), [released]);

  return (
    <V2Modal open title="QR برای چاپ" size="md" onClose={onClose}>
      <div style={{ display: 'grid', gap: 14, justifyItems: 'center' }}>
        <strong style={ltr}>{link.code}</strong>
        {problem ? (
          <Alert tone="danger">
            <strong>فایل چاپ صادر نشد.</strong> {problem} تأیید چاپ باطل شده است؛ آدرس مستقرشده را
            دوباره بررسی و تأیید کنید.
          </Alert>
        ) : null}
        {!released && !problem ? <LoadingState /> : null}
        {released && svg ? (
          <>
            <img
              alt={`QR code برای ${released.url}`}
              width={280}
              height={280}
              style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--sk-border)' }}
              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`}
            />
            <div style={{ ...ltr, fontSize: 14, wordBreak: 'break-all' }}>{released.url}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <V2Button
                variant="secondary"
                onClick={() =>
                  void navigator.clipboard.writeText(released.url).then(
                    () => setCopied(true),
                    () => setCopied(false),
                  )
                }
              >
                {copied ? 'کپی شد' : 'کپی آدرس'}
              </V2Button>
              <V2Button
                variant="primary"
                onClick={() =>
                  download(
                    suggestedFilename(link.code, 'svg'),
                    new Blob([svg], { type: 'image/svg+xml' }),
                  )
                }
              >
                دانلود SVG (برداری، برای چاپ)
              </V2Button>
              <V2Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void qrPngBlob(released.url)
                    .then((b) => download(suggestedFilename(link.code, 'png'), b))
                    .finally(() => setBusy(false));
                }}
              >
                دانلود PNG پرکیفیت (۴۱۰۰ پیکسل)
              </V2Button>
              <a
                className="sk-btn sk-btn--ghost"
                href={`/q/${link.code}`}
                target="_blank"
                rel="noreferrer"
              >
                آزمایش لینک
              </a>
            </div>
            <p
              style={{ margin: 0, fontSize: 12, color: 'var(--sk-fg-muted)', textAlign: 'center' }}
            >
              این صدور (نسخهٔ {released.revision}) در رویدادها ثبت شد. محتوای QR همیشه فقط همین آدرس
              دائمی است. قبل از چاپ نمونه را با دو گوشی (iOS و Android) در اندازهٔ نهایی اسکن کنید و
              روی زمینهٔ روشن با حاشیهٔ سفید چاپ کنید.
            </p>
          </>
        ) : null}
      </div>
    </V2Modal>
  );
}

function EditDialog({
  link,
  onClose,
  onSaved,
}: {
  readonly link: OperatorShortLink | null;
  readonly onClose: () => void;
  readonly onSaved: (saved: OperatorShortLink) => void;
}) {
  const creating = link === null;
  const [code, setCode] = useState(link?.code ?? '');
  const [destination, setDestination] = useState(link?.targetPath ?? '');
  const [note, setNote] = useState(link?.note ?? '');
  const [status, setStatus] = useState<'active' | 'disabled'>(link?.status ?? 'active');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeOk = SHORT_LINK_CODE.test(code);
  const hint = destinationHint(destination);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (creating && !codeOk)
      return setError('کد باید ۲ تا ۴۰ نویسه با حروف کوچک انگلیسی، عدد و خط تیره باشد.');
    if (hint === null) return setError('مقصد باید مسیر داخلی /t/… یا نشانی https:// باشد.');
    setBusy(true);
    setError(null);
    const common = { destination: hint, note: note.trim() === '' ? null : note.trim(), status };
    try {
      onSaved(
        creating
          ? await createOperatorShortLink({ code, ...common })
          : await updateOperatorShortLink(link.code, {
              ...common,
              expectedRevision: link.revision,
            }),
      );
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <V2Modal
      open
      title={creating ? 'کد جدید' : 'ویرایش مقصد'}
      size="md"
      onClose={() => (busy ? undefined : onClose())}
      footer={
        <>
          <V2Button variant="secondary" onClick={onClose} disabled={busy}>
            انصراف
          </V2Button>
          <V2Button variant="primary" type="submit" form="short-link-form" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره'}
          </V2Button>
        </>
      }
    >
      <form
        id="short-link-form"
        onSubmit={(e) => void submit(e)}
        noValidate
        style={{ display: 'grid', gap: 14 }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {!creating ? (
          <Alert tone="warning">
            تغییر مقصد یا غیرفعال‌سازی <strong>فوراً</strong> روی همهٔ QRهای چاپ‌شدهٔ این کد اثر
            می‌گذارد. خود QR و کد تغییر نمی‌کنند و کد قابل حذف یا تغییر نام نیست.
          </Alert>
        ) : null}
        <label style={{ display: 'grid', gap: 4 }}>
          <span>کد (در آدرس QR؛ پس از ساخت دائمی است)</span>
          <input
            className="sk-input"
            style={ltr}
            value={code}
            disabled={!creating}
            maxLength={40}
            placeholder="nashmi"
            onChange={(e) => setCode(e.target.value.trim().toLowerCase())}
          />
          {creating && code !== '' && !codeOk ? (
            <small style={{ color: 'var(--sk-danger)' }}>
              فقط a-z، ۰-۹ و خط تیره؛ ۲ تا ۴۰ نویسه.
            </small>
          ) : null}
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>مقصد</span>
          <input
            className="sk-input"
            style={ltr}
            value={destination}
            placeholder="https://www.salon.ir/…  یا  /t/nashmi/login"
            onChange={(e) => setDestination(e.target.value)}
          />
          <small
            style={{
              color:
                hint !== null || destination === '' ? 'var(--sk-fg-muted)' : 'var(--sk-danger)',
            }}
          >
            مسیر داخلی <bdi style={ltr}>/t/…</bdi> یا آدرس <bdi style={ltr}>https://</bdi> روی
            میزبانی که در «میزبان‌های مجاز» تأیید شده باشد (بدون نام‌کاربری، پورت یا #).
          </small>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>یادداشت (اختیاری)</span>
          <input
            className="sk-input"
            value={note}
            maxLength={200}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>وضعیت</span>
          <select
            className="sk-select"
            value={status}
            onChange={(e) => setStatus(e.target.value === 'disabled' ? 'disabled' : 'active')}
          >
            <option value="active">فعال</option>
            <option value="disabled">غیرفعال (اسکن صفحهٔ «دیگر فعال نیست» می‌دهد)</option>
          </select>
        </label>
      </form>
    </V2Modal>
  );
}
